import { formatName } from "../../../../../utils/format-name.ts";
import clsx from "clsx";
import {
  BriefcaseBusiness,
  Building2,
  Mail,
  User,
  type LucideIcon,
} from "lucide-react";
import { Button } from "../../../../ui/index.ts";
import { Card } from "../../../../shared/card/card.tsx";
import { Avatar } from "../../../../ui/index.ts";
import { useProfileImage } from "../../../../../hooks/use-profile-image.tsx";
import type { OffboardingEmployeeDisplay } from "../../utils/use-offboarding-employee-display.ts";
import styles from "./employee-header-card.module.css";

type InfoBlockConfig = {
    label: string;
    value: string;
    icon: LucideIcon;
    iconTone: "blue" | "green" | "purple";
};

function ProfileAvatar({
    employeeId,
    name,
}: {
    employeeId: string;
    name: string;
}) {
    const profileImage = useProfileImage(
        employeeId !== "—" ? employeeId : undefined,
    );

    return (
        <Avatar
            name={name}
            src={profileImage ?? undefined}
            size="xl"
            className={styles.avatar}
        />
    );
}

function InfoBlock({ label, value, icon: Icon, iconTone }: InfoBlockConfig) {
    return (
        <div className={styles.infoBlock}>
            <span
                className={clsx(
                    styles.infoIconCircle,
                    iconTone === "blue" && styles.infoIconBlue,
                    iconTone === "green" && styles.infoIconGreen,
                    iconTone === "purple" && styles.infoIconPurple,
                )}
                aria-hidden
            >
                <Icon size={18} strokeWidth={2} />
            </span>
            <div className={styles.infoText}>
                <span className={styles.infoLabel}>{label}</span>
                <span className={styles.infoValue} title={value}>
                    {value}
                </span>
            </div>
        </div>
    );
}

function formatEmployeeSubType(value: string): string {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, "");
  if (normalized === "fulltime" || normalized === "fulltimie") {
    return "Full Time";
  }
  return value;
}

export interface EmployeeHeaderCardProps {
    employee: OffboardingEmployeeDisplay;
    lastWorkingDay: string;
    showViewResignationForm?: boolean;
    onViewResignationForm?: () => void;
    className?: string;
}

export function EmployeeHeaderCard({
    employee,
    lastWorkingDay,
    showViewResignationForm = true,
    onViewResignationForm, 
    className,
}: EmployeeHeaderCardProps) {

  const hasLastWorkingDay =
    lastWorkingDay && lastWorkingDay.trim() !== "" && lastWorkingDay !== "TBD";
 
  const infoBlocks: InfoBlockConfig[] = [
    {
      label: "Work Email",
      value: employee.email,
      icon: Mail,
      iconTone: "purple",
    },
    {
      label: "Department",
      value: employee.department,
      icon: Building2,
      iconTone: "blue",
    },
    {
      label: "Employee Sub Type",
      value: formatEmployeeSubType(employee.empType),
      icon: BriefcaseBusiness,
      iconTone: "purple",
    },
    {
      label: "Reporting Manager",
      value: formatName(employee.manager),
      icon: User,
      iconTone: "green",
    },
  ];

  return (
    <Card className={clsx(styles.profileCard, className)}>
      <div className={styles.profileTop}>
        <div className={styles.profileMain}>
          <ProfileAvatar
            employeeId={employee.employeeId}
            name={formatName(employee.name)}
          />
          <div className={styles.profileInfo}>
            <h2 className="heading-sm">{formatName(employee.name)}</h2>
            <p className={clsx("body-sm", styles.designation)}>
              {employee.designation}
            </p>
          </div>
        </div>
        <div className={styles.profileAction}>
          {hasLastWorkingDay && (
            <div className={styles.lwdBlock}>
              <span className={styles.lwdValue}>
                Last Working Day: {lastWorkingDay}
              </span>
            </div>
          )}
          {showViewResignationForm && onViewResignationForm != null && (
            <Button
              variant="link"
              className={styles.viewFormBtn}
              onClick={onViewResignationForm}
            >
              View Submitted Request
            </Button>
          )}
        </div>
      </div>

      <div className={styles.infoStrip}>
        {infoBlocks.map((block, index) => (
          <div key={block.label} className={styles.infoStripItem}>
            {index > 0 && <span className={styles.infoDivider} aria-hidden />}
            <InfoBlock {...block} />
          </div>
        ))}
      </div>
    </Card>
  );
}
